import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Divider,
  CircularProgress,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  fetchPagarmePublicBootstrap,
  submitPagarmePublicOnboarding,
  PAGARME_ONBOARDING_TOKEN_STORAGE_KEY,
  type PublicPagarmeBootstrap,
} from '../../services/publicPagarmeParishOnboardingApi';
import { RootState } from '../../store';
import { cleanDocument, formatCpfCnpjInput } from '../../utils/cpfCnpjValidator';
import {
  inferRegisterTypeFromDocument,
  looksLikePersonName,
  validatePagarmeRecipientForm,
  PAGARME_BANK_HOLDER_NAME_MAX,
  type PagarmeRecipientFormErrors,
  type RegisterType,
} from '../../utils/pagarmeRecipientFormValidation';

const PIX_TYPES = [
  { value: 'cpf', label: 'CPF' },
  { value: 'cnpj', label: 'CNPJ' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Telefone' },
  { value: 'random', label: 'Chave aleatória' },
] as const;

/**
 * Formulário para regularizar recebedor Pagarme: exige login como parish_admin da paróquia do link.
 */
const RegularizarPagarmeParishPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading } = useSelector((state: RootState) => state.auth);
  const urlToken = searchParams.get('t') || '';
  const [token, setToken] = useState(() => {
    if (urlToken) return urlToken;
    return sessionStorage.getItem(PAGARME_ONBOARDING_TOKEN_STORAGE_KEY) || '';
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [bootstrap, setBootstrap] = useState<PublicPagarmeBootstrap | null>(null);
  const [busy, setBusy] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<PagarmeRecipientFormErrors>({});
  const [parishDisplayName, setParishDisplayName] = useState('');

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [stateUf, setStateUf] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [cpfCnpjType, setCpfCnpjType] = useState<'cpf' | 'cnpj'>('cnpj');

  const [registerType, setRegisterType] = useState<RegisterType>('individual');
  const [birthdate, setBirthdate] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [tradingName, setTradingName] = useState('');
  const [annualRevenue, setAnnualRevenue] = useState('');
  const [foundingDate, setFoundingDate] = useState('');
  const [repName, setRepName] = useState('');
  const [repEmail, setRepEmail] = useState('');
  const [repDocument, setRepDocument] = useState('');
  const [repBirthdate, setRepBirthdate] = useState('');
  const [repPhone, setRepPhone] = useState('');

  const [holderName, setHolderName] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [branchNumber, setBranchNumber] = useState('');
  const [branchCheckDigit, setBranchCheckDigit] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountCheckDigit, setAccountCheckDigit] = useState('');
  const [accountKind, setAccountKind] = useState<'checking' | 'savings'>('checking');

  const [streetExtra, setStreetExtra] = useState('');
  const [streetNumber, setStreetNumber] = useState('');
  const [neighborhoodExtra, setNeighborhoodExtra] = useState('');
  const [cepExtra, setCepExtra] = useState('');
  const [refPoint, setRefPoint] = useState('');

  const [pixKeyType, setPixKeyType] = useState<(typeof PIX_TYPES)[number]['value']>('email');
  const [pixKey, setPixKey] = useState('');
  const [hadPixConfigured, setHadPixConfigured] = useState(false);

  const [syncWithPagarme, setSyncWithPagarme] = useState(true);

  const parishDocDigits = useMemo(() => cleanDocument(cpfCnpj), [cpfCnpj]);
  const holderDocumentSynced = useMemo(() => {
    const d = parishDocDigits;
    if (d.length === 11 || d.length === 14) return formatCpfCnpjInput(d);
    return '';
  }, [parishDocDigits]);
  const effectiveRegisterType: RegisterType =
    cpfCnpjType === 'cnpj' || parishDocDigits.length === 14 ? 'corporation' : registerType;
  const effectiveCorporateHolder = useMemo(
    () => holderName.trim() || companyName.trim().slice(0, PAGARME_BANK_HOLDER_NAME_MAX),
    [companyName, holderName]
  );

  useEffect(() => {
    if (cpfCnpjType === 'cnpj' || parishDocDigits.length === 14) {
      setRegisterType('corporation');
    }
  }, [cpfCnpjType, parishDocDigits.length]);

  const applyCorporateHolder = (name: string) => {
    setCompanyName(name);
    setHolderName(name.trim().slice(0, PAGARME_BANK_HOLDER_NAME_MAX));
    setFieldErrors((prev) => ({ ...prev, companyName: undefined, holderName: undefined }));
  };

  useEffect(() => {
    if (!urlToken) return;
    sessionStorage.setItem(PAGARME_ONBOARDING_TOKEN_STORAGE_KEY, urlToken);
    setToken(urlToken);
    navigate('/regularizar-pagarme', { replace: true });
  }, [urlToken, navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login?next=/regularizar-pagarme', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  const hydrateFromBootstrap = useCallback((d: PublicPagarmeBootstrap) => {
    setParishDisplayName(String(d.parishName ?? ''));
    const b = d.parishBasics as Record<string, string>;
    setEmail(String(b.email ?? ''));
    setPhone(String(b.phone ?? ''));
    setAddress(String(b.address ?? ''));
    setCity(String(b.city ?? ''));
    setStateUf(String(b.state ?? ''));
    setZipCode(String(b.zipCode ?? ''));
    setCpfCnpj(String(b.cpfCnpj ?? ''));
    const ct = String(b.cpfCnpjType ?? '').toLowerCase() === 'cpf' ? 'cpf' : 'cnpj';
    setCpfCnpjType(ct);

    const prof = (d.recipientProfile || {}) as Record<string, unknown>;
    const inferred = inferRegisterTypeFromDocument(cleanDocument(String(b.cpfCnpj ?? '')));
    if (prof.registerType === 'individual' || prof.registerType === 'corporation') {
      setRegisterType(ct === 'cnpj' ? 'corporation' : prof.registerType);
    } else if (inferred) {
      setRegisterType(inferred);
    } else {
      setRegisterType(ct === 'cnpj' ? 'corporation' : 'individual');
    }

    const ind = (prof.individual || {}) as Record<string, string>;
    setBirthdate(ind.birthdate || '');
    const corp = (prof.corporation || {}) as Record<string, unknown>;
    setAnnualRevenue(String(corp.annualRevenue ?? ''));
    setFoundingDate(String(corp.foundingDate ?? ''));
    const rep = (corp.representative || {}) as Record<string, string>;
    setRepName(rep.name || '');
    setRepEmail(rep.email || String(b.email ?? '').trim());
    setRepDocument(rep.document || '');
    setRepBirthdate(rep.birthdate || '');
    setRepPhone(rep.phone || '');

    const ba = (prof.bankAccount || {}) as Record<string, string>;
    const loadedHolder = (ba.holderName || '').trim();
    const loadedCompany = String(corp.companyName ?? '').trim();
    const loadedTrading = String(corp.tradingName ?? '').trim();
    if (cleanDocument(String(b.cpfCnpj ?? '')).length === 14) {
      const corpName =
        loadedCompany ||
        (loadedHolder && !looksLikePersonName(loadedHolder) ? loadedHolder : '') ||
        loadedTrading;
      setCompanyName(corpName);
      setTradingName(loadedTrading || corpName);
      setHolderName(
        (loadedHolder && loadedHolder.length <= PAGARME_BANK_HOLDER_NAME_MAX
          ? loadedHolder
          : corpName.slice(0, PAGARME_BANK_HOLDER_NAME_MAX)) || loadedHolder.slice(0, PAGARME_BANK_HOLDER_NAME_MAX)
      );
    } else {
      setCompanyName(loadedCompany);
      setTradingName(loadedTrading);
      setHolderName(loadedHolder);
    }
    setBankCode(ba.bank || '');
    setBranchNumber(ba.branchNumber || '');
    setBranchCheckDigit(String(ba.branchCheckDigit ?? ''));
    setAccountNumber(ba.accountNumber || '');
    setAccountCheckDigit(String(ba.accountCheckDigit ?? ''));
    if (ba.type === 'savings') setAccountKind('savings');
    else setAccountKind('checking');

    const addr = (prof.addressOverride || {}) as Record<string, string>;
    setStreetExtra(addr.street || '');
    setStreetNumber(addr.streetNumber || '');
    setNeighborhoodExtra(addr.neighborhood || '');
    setCepExtra(addr.zipCode || '');
    setRefPoint(addr.referencePoint || '');

    const wd = d.withdrawalData as { pixKeyType?: string } | undefined;
    const pxt = wd?.pixKeyType as (typeof PIX_TYPES)[number]['value'] | undefined;
    if (pxt && PIX_TYPES.some((x) => x.value === pxt)) setPixKeyType(pxt);
    setHadPixConfigured(d.hasPixConfigured);
    setPixKey('');
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (authLoading || !isAuthenticated) return;
      if (!token) {
        setLoading(false);
        setError('Abra o link completo enviado pela equipe (parâmetro t na URL).');
        return;
      }
      if (user?.userType && user.userType !== 'parish_admin') {
        setLoading(false);
        setError('Apenas o administrador da paróquia pode usar este formulário.');
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await fetchPagarmePublicBootstrap(token);
        if (!res.success || !res.data) throw new Error('Resposta inválida');
        if (cancelled) return;
        setBootstrap(res.data);
        hydrateFromBootstrap(res.data);
      } catch (e: any) {
        if (!cancelled) {
          setError(e.response?.data?.message || e.message || 'Link inválido ou expirado.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [token, hydrateFromBootstrap, authLoading, isAuthenticated, user?.userType]);

  const buildRecipientPayload = (): Record<string, unknown> => {
    const corpNameTrimmed = companyName.trim();
    const bankHolderName =
      effectiveRegisterType === 'corporation'
        ? holderName.trim() || corpNameTrimmed.slice(0, PAGARME_BANK_HOLDER_NAME_MAX)
        : holderName.trim();

    return {
      registerType: effectiveRegisterType,
      individual: { birthdate },
      corporation: {
        companyName: corpNameTrimmed || bankHolderName,
        tradingName: tradingName.trim() || corpNameTrimmed || bankHolderName,
        annualRevenue,
        foundingDate: foundingDate.trim() ? foundingDate : undefined,
        representative: {
          name: repName.trim(),
          email: repEmail.trim(),
          document: repDocument.trim(),
          birthdate: repBirthdate.trim(),
          phone: repPhone.trim() || undefined,
          selfDeclaredRepresentative: true,
        },
      },
      bankAccount: {
        holderName: bankHolderName,
        holderDocument: holderDocumentSynced.trim(),
        bank: bankCode,
        branchNumber,
        branchCheckDigit,
        accountNumber,
        accountCheckDigit,
        type: accountKind,
      },
      addressOverride:
        streetExtra.trim() ||
        streetNumber.trim() ||
        neighborhoodExtra.trim() ||
        cepExtra.trim() ||
        refPoint.trim()
          ? {
              street: streetExtra || undefined,
              streetNumber: streetNumber || undefined,
              neighborhood: neighborhoodExtra || undefined,
              zipCode: cepExtra || undefined,
              referencePoint: refPoint || undefined,
            }
          : undefined,
    };
  };

  const runClientValidation = (): boolean => {
    const corpNameTrimmed = companyName.trim();
    const bankHolderName =
      effectiveRegisterType === 'corporation'
        ? holderName.trim() || corpNameTrimmed.slice(0, PAGARME_BANK_HOLDER_NAME_MAX)
        : holderName.trim();

    const { errors, blockingMessages } = validatePagarmeRecipientForm({
      registerType: effectiveRegisterType,
      parishDocDigits,
      parishCpfCnpjType: cpfCnpjType,
      parishName: parishDisplayName,
      holderName: bankHolderName,
      companyName: corpNameTrimmed,
      tradingName,
      birthdate,
      repBirthdate,
      repName,
      repEmail,
      repDocument,
      sameRepAsHolder: false,
      bankCode,
      branchNumber,
      accountNumber,
      accountCheckDigit,
    });
    setFieldErrors(errors);
    if (blockingMessages.length) {
      setError(blockingMessages[0]);
      return false;
    }
    setError(null);
    setFieldErrors({});
    return true;
  };

  const handleSubmit = async () => {
    if (!token) return;
    if (!runClientValidation()) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const keyTrim = pixKey.trim();
      const payload: Record<string, unknown> = {
        token,
        parishContact: {
          email: email.trim(),
          phone: phone.trim(),
          address: address.trim(),
          city: city.trim(),
          state: stateUf.trim().toUpperCase().slice(0, 2),
          zipCode: zipCode.trim(),
        },
        recipientProfile: buildRecipientPayload(),
        syncWithPagarme: syncWithPagarme,
      };
      if (keyTrim || hadPixConfigured) {
        payload.withdrawalData = { pixKeyType, pixKey: keyTrim };
      }

      const res = await submitPagarmePublicOnboarding(payload);
      if (!res.success) throw new Error('Falha ao salvar');
      setSuccess(res.message || 'Salvo com sucesso.');
      if (res.data && typeof res.data === 'object') {
        const pend = (res.data as any).pendencies as string[] | undefined;
        if (bootstrap && Array.isArray(pend)) {
          setBootstrap({ ...bootstrap, pendencies: pend });
        }
      }
    } catch (e: any) {
      const msg = e.response?.data?.message || e.message || 'Erro ao enviar';
      setError(msg);
      const pend = e.response?.data?.data?.pendencies as string[] | undefined;
      if (bootstrap && Array.isArray(pend)) setBootstrap({ ...bootstrap, pendencies: pend });
    } finally {
      setBusy(false);
    }
  };

  if (authLoading || !isAuthenticated) {
    return (
      <Box py={8} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box py={8} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom color="primary">
          Regularização Pagarme — {bootstrap?.parishName || 'Paróquia'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Entre com a conta de administrador da paróquia para alterar dados de recebimento (split) e chave Pix.
          Com CNPJ, a <strong>razão social</strong> e o representante legal são obrigatórios. Não compartilhe o link
          original.
        </Typography>
        {bootstrap?.expiresAt ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Validade do link: {new Date(bootstrap.expiresAt).toLocaleString('pt-BR')}
          </Typography>
        ) : null}

        {!!error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!!success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {!token || !bootstrap ? null : (
          <>
            {bootstrap.pendencies?.length ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={700}>
                  Pendências
                </Typography>
                <ul style={{ margin: '8px 0 0 18px', padding: 0 }}>
                  {bootstrap.pendencies.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </Alert>
            ) : null}

            <Typography variant="subtitle2" gutterBottom>
              Documento da paróquia
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Somente leitura — alteração de CPF/CNPJ exige suporte da equipe.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 2, mb: 2 }}>
              <TextField
                select
                size="small"
                label="Tipo"
                value={cpfCnpjType}
                InputProps={{ readOnly: true }}
                disabled
              >
                <MenuItem value="cnpj">CNPJ</MenuItem>
                <MenuItem value="cpf">CPF</MenuItem>
              </TextField>
              <TextField
                size="small"
                label="Número do documento"
                value={cpfCnpj}
                InputProps={{ readOnly: true }}
                disabled
                helperText="Vinculado ao cadastro da paróquia."
              />
            </Box>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Contato e endereço
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
              <TextField label="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} size="small" />
              <TextField label="Telefone (DDD + número)" value={phone} onChange={(e) => setPhone(e.target.value)} size="small" />
              <TextField label="Logradouro" value={address} onChange={(e) => setAddress(e.target.value)} size="small" />
              <TextField label="Cidade" value={city} onChange={(e) => setCity(e.target.value)} size="small" />
              <TextField label="UF" value={stateUf} onChange={(e) => setStateUf(e.target.value)} size="small" inputProps={{ maxLength: 2 }} />
              <TextField label="CEP" value={zipCode} onChange={(e) => setZipCode(e.target.value)} size="small" helperText="8 dígitos — ex.: 65208000" />
            </Box>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Tipo de recebedor
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              PJ (CNPJ): razão social, representante legal e conta bancária são obrigatórios.
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={effectiveRegisterType}
              onChange={(_, v) => {
                if (!v) return;
                if (cpfCnpjType === 'cnpj' && v === 'individual') return;
                setRegisterType(v);
              }}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="corporation">Pessoa jurídica</ToggleButton>
              <ToggleButton value="individual" disabled={cpfCnpjType === 'cnpj'}>
                Pessoa física
              </ToggleButton>
            </ToggleButtonGroup>

            {effectiveRegisterType === 'corporation' ? (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Com CNPJ, o titular da conta no banco deve ser a <strong>razão social</strong> (igual ao extrato) — não
                  o nome de uma pessoa física.
                </Alert>
                <Typography variant="subtitle2" gutterBottom>
                  Dados da empresa (obrigatório)
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                  <TextField
                    label="Razão social"
                    value={companyName}
                    onChange={(e) => applyCorporateHolder(e.target.value)}
                    size="small"
                    required
                    error={!!fieldErrors.companyName}
                    helperText={
                      fieldErrors.companyName ||
                      'Nome completo da empresa (CNPJ). O titular do banco pode ser encurtado abaixo.'
                    }
                  />
                  <TextField
                    label="Nome fantasia"
                    value={tradingName}
                    onChange={(e) => setTradingName(e.target.value)}
                    size="small"
                    helperText="Opcional — se vazio, usamos a razão social."
                  />
                </Box>
              </>
            ) : null}

            <Typography variant="subtitle2" gutterBottom>
              Conta bancária
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
              {effectiveRegisterType === 'corporation' ? (
                <TextField
                  label="Titular enviado ao banco"
                  value={holderName}
                  onChange={(e) => {
                    setHolderName(e.target.value.slice(0, PAGARME_BANK_HOLDER_NAME_MAX));
                    setFieldErrors((prev) => ({ ...prev, holderName: undefined }));
                  }}
                  size="small"
                  required
                  inputProps={{ maxLength: PAGARME_BANK_HOLDER_NAME_MAX }}
                  error={!!fieldErrors.holderName || effectiveCorporateHolder.length > PAGARME_BANK_HOLDER_NAME_MAX}
                  helperText={
                    fieldErrors.holderName ||
                    `Nome curto enviado ao Pagar.me (máx. ${PAGARME_BANK_HOLDER_NAME_MAX}). ${holderName.length}/${PAGARME_BANK_HOLDER_NAME_MAX}`
                  }
                />
              ) : (
                <TextField
                  label="Titular *"
                  value={holderName}
                  onChange={(e) => setHolderName(e.target.value.slice(0, PAGARME_BANK_HOLDER_NAME_MAX))}
                  size="small"
                  required
                  inputProps={{ maxLength: PAGARME_BANK_HOLDER_NAME_MAX }}
                  error={!!fieldErrors.holderName}
                  helperText={
                    fieldErrors.holderName ||
                    `Máx. ${PAGARME_BANK_HOLDER_NAME_MAX} caracteres. ${holderName.length}/${PAGARME_BANK_HOLDER_NAME_MAX}`
                  }
                />
              )}
              <TextField
                label="Doc. titular"
                value={holderDocumentSynced}
                size="small"
                InputProps={{ readOnly: true }}
                helperText="Igual ao CPF/CNPJ informado acima."
              />
              <TextField
                label="Código do banco"
                value={bankCode}
                onChange={(e) => setBankCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
                size="small"
                required
                error={!!fieldErrors.bankCode}
                helperText={fieldErrors.bankCode || '3 dígitos — ex.: 077 Inter'}
              />
              <TextField
                label="Agência"
                value={branchNumber}
                onChange={(e) => setBranchNumber(e.target.value)}
                size="small"
                required
                error={!!fieldErrors.branchNumber}
                helperText={fieldErrors.branchNumber}
              />
              <TextField label="Díg. agência" value={branchCheckDigit} onChange={(e) => setBranchCheckDigit(e.target.value)} size="small" />
              <TextField
                label="Conta"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                size="small"
                required
                error={!!fieldErrors.accountNumber}
                helperText={fieldErrors.accountNumber}
              />
              <TextField
                label="Díg. conta"
                value={accountCheckDigit}
                onChange={(e) => setAccountCheckDigit(e.target.value)}
                size="small"
                required
                error={!!fieldErrors.accountCheckDigit}
                helperText={fieldErrors.accountCheckDigit || 'Pode ser 0 ou X.'}
              />
              <TextField select label="Tipo conta" value={accountKind} onChange={(e) => setAccountKind(e.target.value as 'checking' | 'savings')} size="small">
                <MenuItem value="checking">Corrente</MenuItem>
                <MenuItem value="savings">Poupança</MenuItem>
              </TextField>
            </Box>

            {effectiveRegisterType === 'individual' ? (
              <TextField
                label="Data de nascimento do titular (DD/MM/AAAA)"
                value={birthdate}
                onChange={(e) => setBirthdate(e.target.value)}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
                required
                error={!!fieldErrors.birthdate}
                helperText={fieldErrors.birthdate}
              />
            ) : (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Representante legal (PJ) *
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Pessoa física que assina pela entidade — diferente da razão social do titular da conta.
                </Alert>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Nome do representante"
                    value={repName}
                    onChange={(e) => setRepName(e.target.value)}
                    size="small"
                    required
                    error={!!fieldErrors.repName}
                    helperText={fieldErrors.repName}
                  />
                  <TextField
                    label="E-mail do representante"
                    value={repEmail}
                    onChange={(e) => setRepEmail(e.target.value)}
                    size="small"
                    required
                    error={!!fieldErrors.repEmail}
                    helperText={fieldErrors.repEmail}
                  />
                  <TextField
                    label="CPF do representante"
                    value={repDocument}
                    onChange={(e) => setRepDocument(e.target.value)}
                    size="small"
                    required
                    error={!!fieldErrors.repDocument}
                    helperText={fieldErrors.repDocument}
                  />
                  <TextField label="Telefone do representante" value={repPhone} onChange={(e) => setRepPhone(e.target.value)} size="small" />
                  <TextField
                    label="Nascimento do representante (DD/MM/AAAA)"
                    value={repBirthdate}
                    onChange={(e) => setRepBirthdate(e.target.value)}
                    size="small"
                    required
                    error={!!fieldErrors.repBirthdate}
                    helperText={fieldErrors.repBirthdate}
                  />
                </Box>
                <Accordion sx={{ mt: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">Dados extras da empresa (opcional)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField label="Faturamento anual (centavos)" value={annualRevenue} onChange={(e) => setAnnualRevenue(e.target.value.replace(/\D/g, ''))} size="small" />
                      <TextField label="Data de fundação (DD/MM/AAAA)" value={foundingDate} onChange={(e) => setFoundingDate(e.target.value)} size="small" helperText="Ex.: 08/04/2025" />
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Endereço extra (opcional)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Use para informar bairro (ex.: Boa Esperança) se diferente do cadastro principal.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField label="Logradouro" value={streetExtra} onChange={(e) => setStreetExtra(e.target.value)} size="small" />
                  <TextField label="Número" value={streetNumber} onChange={(e) => setStreetNumber(e.target.value)} size="small" />
                  <TextField label="Bairro" value={neighborhoodExtra} onChange={(e) => setNeighborhoodExtra(e.target.value)} size="small" />
                  <TextField label="CEP" value={cepExtra} onChange={(e) => setCepExtra(e.target.value)} size="small" />
                  <TextField label="Referência" value={refPoint} onChange={(e) => setRefPoint(e.target.value)} size="small" />
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion sx={{ mt: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Chave Pix (para saque)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Opcional para criar o recebedor no Pagarme; necessária para solicitar saque depois.
                </Alert>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField select label="Tipo Pix" value={pixKeyType} onChange={(e) => setPixKeyType(e.target.value as typeof pixKeyType)} size="small">
                    {PIX_TYPES.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Chave"
                    value={pixKey}
                    onChange={(e) => setPixKey(e.target.value)}
                    size="small"
                    helperText={hadPixConfigured && !pixKey.trim() ? 'Deixe vazio para manter a chave já cadastrada.' : undefined}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            <FormControlLabel
              sx={{ mt: 2 }}
              control={<Checkbox checked={syncWithPagarme} onChange={(e) => setSyncWithPagarme(e.target.checked)} />}
              label="Sincronizar com o Pagar.me ao salvar (recomendado)"
            />

            <Box sx={{ mt: 2 }}>
              <Button variant="contained" disabled={busy} onClick={() => handleSubmit()} fullWidth size="large">
                {busy ? <CircularProgress size={22} color="inherit" /> : 'Salvar'}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default RegularizarPagarmeParishPage;
